/**
 * MiniProjectBomItemCard — visual rendering of a single BOM line item.
 *
 * Sister to MiniProjectGoalCard and MiniProjectTaskCard. State pill
 * cycles on click for Leads (needed → obtained → not_applicable →
 * needed). Template items can't be deleted but their state is mutable.
 *
 * Layout:
 *   - Header: state pill + name + template badge + pencil
 *   - Meta: quantity × unit + cost + purchase link
 *   - Notes (if any) on a subdued line
 *
 * Strings localized via `pages:miniProject.cards.bom.*`
 * (Chunk D Phase 3 sweep, 2026-05-22).
 */

import React, { useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  updateMiniProjectBomItem,
  type MiniProjectBomItem,
} from "../../api/miniProjects";
import styles from "./MiniProjectBomItemCard.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  miniProjectGuid: string;
  item: MiniProjectBomItem;
  isLead: boolean;
  onMutated: () => void;
  onEdit: () => void;
}

const NEXT_STATE: Record<
  MiniProjectBomItem["procurementState"],
  MiniProjectBomItem["procurementState"]
> = {
  needed: "obtained",
  obtained: "not_applicable",
  not_applicable: "needed",
};

// State → i18n label key. Resolved at render so labels localize.
const STATE_LABEL_KEY: Record<MiniProjectBomItem["procurementState"], string> = {
  needed: "miniProject.cards.bom.state.needed",
  obtained: "miniProject.cards.bom.state.obtained",
  not_applicable: "miniProject.cards.bom.state.notApplicable",
};

/* ── Helpers ───────────────────────────────────────────────────── */

function formatCost(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toFixed(0)} ${currency}`;
  }
}

/* ── Inline icons ──────────────────────────────────────────────── */

const PencilIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const LinkIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────── */

const MiniProjectBomItemCard: React.FC<Props> = ({
  miniProjectGuid,
  item,
  isLead,
  onMutated,
  onEdit,
}) => {
  const { t } = useAppTranslation("pages");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function cycleState() {
    if (!isLead || saving) return;
    setError(null);
    setSaving(true);
    try {
      await updateMiniProjectBomItem(miniProjectGuid, item.bomItemGuid, {
        procurementState: NEXT_STATE[item.procurementState],
      });
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const hasMeta =
    item.quantity != null ||
    item.estimatedCost != null ||
    item.purchaseLink;

  return (
    <div className={styles.card} data-state={item.procurementState}>
      {/* ── Header ── */}
      <div className={styles.headerRow}>
        <button
          type="button"
          className={`${styles.statePill} ${styles[`state_${item.procurementState}`]}`}
          onClick={cycleState}
          disabled={!isLead || saving}
          title={
            isLead
              ? t("miniProject.cards.bom.markAs", { state: t(STATE_LABEL_KEY[NEXT_STATE[item.procurementState]]) })
              : t(STATE_LABEL_KEY[item.procurementState])
          }
        >
          {t(STATE_LABEL_KEY[item.procurementState])}
        </button>
        <div className={styles.titleArea}>
          <span className={styles.name}>{item.name}</span>
          {item.isTemplateItem && (
            <span className={styles.badge}>{t("miniProject.cards.bom.templateBadge")}</span>
          )}
        </div>
        {isLead && (
          <button
            type="button"
            className={styles.editBtn}
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            aria-label={t("miniProject.cards.bom.editAria")}
            title={t("miniProject.cards.bom.editTitle")}
          >
            <PencilIcon />
          </button>
        )}
      </div>

      {/* ── Meta row: quantity, cost, link ── */}
      {hasMeta && (
        <div className={styles.metaRow}>
          {item.quantity != null && (
            <span className={styles.metaItem}>
              {item.quantity}
              {item.unit ? ` ${item.unit}` : ""}
            </span>
          )}
          {item.estimatedCost != null && (
            <span className={styles.metaItem}>
              {formatCost(
                item.estimatedCost * (item.quantity ?? 1),
                item.currencyCode || "USD",
              )}
            </span>
          )}
          {item.purchaseLink && (
            <a
              href={item.purchaseLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.linkChip}
            >
              <LinkIcon />
              {t("miniProject.cards.bom.linkLabel")}
            </a>
          )}
        </div>
      )}

      {/* ── Notes (if any) ── */}
      {item.notes && <div className={styles.notes}>{item.notes}</div>}

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default MiniProjectBomItemCard;
